// TopNav — the redesign chrome (approved prototype, port step 2): brand roundel +
// "your company, in sync", pill nav (Briefing · Actions · Ask · Live · Knowledge ·
// People · Sources), iOS-style red badge on Actions, search / theme / account on the
// right. Markup is Tailwind under the `.rd` root (redesign.css); behavior (menu
// close-on-outside-click, sign out, settings entry) is unchanged from the pre-port bar.

import { useState, useRef, useEffect } from 'react';
import { Moon, Search, Sun } from 'lucide-react';
import { TerumLogo } from '../shared/TerumLogo';
import type { Theme } from '../../lib/theme';
import { topLevelSurfaces, activeNavId } from '../../lib/surfaces';
import { useNeedsYouCount } from '../../hooks/useNeedsYouCount';

const NAV = topLevelSurfaces();

interface TopNavProps {
  route: string;
  theme: Theme;
  onNav: (id: string) => void;
  onToggleTheme: () => void;
  onCmdK: () => void;
  profile?: { name: string; initials: string; email?: string };
  onSignOut: () => void;
  isMobile?: boolean;
}

export function TopNav({ route, theme, onNav, onToggleTheme, onCmdK, profile, onSignOut, isMobile }: TopNavProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // A child route lights its parent's entry — a note reads as Knowledge, an archived briefing as
  // Briefing. Without this the whole pill row goes grey on 5 of the app's reachable screens.
  const activeId = activeNavId(route);

  const needsYou = useNeedsYouCount();

  // Close the account menu on outside-click or Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <header className="rd border-b border-border bg-background font-['DM_Sans'] text-foreground">
      <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-4 px-4 py-3 sm:px-7 lg:px-10">
        <button className="group flex shrink-0 items-center gap-3 text-left" onClick={() => onNav('briefing')} aria-label="Terum home">
          <span className="grid size-10 place-items-center rounded-full bg-primary text-primary-foreground shadow-[0_7px_18px_rgba(74,58,107,.22)] transition group-hover:-rotate-6">
            <TerumLogo size={22} />
          </span>
          <span>
            {/* The wordmark matches terum.ai's `.brandword` lockup — uppercase, 600, .28em tracking
              * (Teddy 2026-07-30). Family is DM Sans here; the site sets the same treatment in Satoshi. */}
            <span className="block text-[16px] font-semibold uppercase leading-5 tracking-[.28em]">Terum</span>
            <span className="mt-1 block font-['DM_Mono'] text-[9px] uppercase tracking-[.18em] text-muted-foreground">your company, in sync</span>
          </span>
        </button>

        {!isMobile && (
          // bug-720: below lg the header's min-content (~915px) exceeded the viewport and — via
          // the app shell's grid floor — dragged the whole page wide, clipping every band's right
          // edge. The pill row is the one shrinkable child: it scrolls horizontally at narrow
          // widths (a deliberate scroller) and is untouched at desktop.
          <nav className="flex min-w-0 items-center gap-1 rounded-full border border-border bg-card p-1 max-lg:overflow-x-auto">
            {NAV.map((it) => (
              <button
                key={it.id}
                onClick={() => onNav(it.id)}
                aria-current={activeId === it.id ? 'page' : undefined}
                className={`relative rounded-full px-4 py-1.5 text-sm transition ${activeId === it.id ? 'bg-foreground text-card' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {it.label}
                {it.id === 'actions' && needsYou > 0 && (
                  <span className="absolute -right-1.5 -top-1.5 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-[#ff3b30] px-1 text-[10.5px] font-bold leading-none text-white shadow-[0_1px_4px_rgba(0,0,0,.3)] ring-2 ring-card">
                    {needsYou}
                  </span>
                )}
              </button>
            ))}
          </nav>
        )}

        <div className="flex shrink-0 items-center gap-2">
          <button
            className="grid size-9 place-items-center rounded-full border border-border bg-card text-muted-foreground transition hover:shadow-md"
            onClick={onCmdK}
            aria-label="Search"
            title="Search (Ctrl+K)"
          >
            <Search size={16} />
          </button>
          <button
            className="grid size-9 place-items-center rounded-full border border-border bg-card text-muted-foreground transition hover:shadow-md"
            onClick={onToggleTheme}
            aria-label="Toggle theme"
            title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          {/* Account menu — identity, Settings, and the app's only Sign out. */}
          <div className="relative" ref={menuRef}>
            <button
              className={`grid size-9 place-items-center rounded-full bg-[#d9b28e] text-sm font-semibold text-[#4a372b] shadow-[0_4px_9px_rgba(121,87,58,.18)] transition ${menuOpen || activeId === 'settings' ? 'ring-2 ring-foreground/40' : ''}`}
              onClick={() => setMenuOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="Account menu"
              title={profile?.email || 'Account'}
            >
              {profile?.initials || '·'}
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 w-60 overflow-hidden rounded-2xl border border-border bg-card p-1.5 shadow-[0_18px_40px_rgba(48,38,25,.18)]" role="menu">
                <div className="border-b border-border px-3 pb-2.5 pt-2">
                  <span className="block text-sm font-semibold leading-5">{profile?.name || 'Signed in'}</span>
                  {profile?.email && <span className="mt-0.5 block truncate text-xs text-muted-foreground">{profile.email}</span>}
                </div>
                {isMobile && (
                  <button className="mt-1 flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm transition hover:bg-muted" role="menuitem"
                    onClick={() => { setMenuOpen(false); onNav('sources'); }}>
                    Sources
                  </button>
                )}
                <button className="mt-1 flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm transition hover:bg-muted" role="menuitem"
                  onClick={() => { setMenuOpen(false); onNav('settings'); }}>
                  Settings
                </button>
                <button className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-destructive transition hover:bg-muted" role="menuitem"
                  onClick={() => { setMenuOpen(false); onSignOut(); }}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
